import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Legacy variants
        sky: "bg-sky-500 text-white font-bold uppercase text-xs shadow hover:shadow-md active:bg-sky-600 transition-all duration-150 ease-linear outline-none focus:outline-none",
        gray: "bg-gray-400 text-white font-bold uppercase text-xs shadow hover:shadow-md active:bg-gray-600 transition-all duration-150 ease-linear outline-none focus:outline-none",
        success:
          "bg-green-600 text-white font-bold uppercase text-xs shadow hover:shadow-md active:bg-green-700 transition-all duration-150 ease-linear outline-none focus:outline-none",
        warning:
          "bg-orange-500 text-white font-bold uppercase text-xs shadow hover:shadow-md active:bg-orange-600 transition-all duration-150 ease-linear outline-none focus:outline-none",
        danger:
          "bg-red-600 text-white font-bold uppercase text-xs shadow hover:shadow-md active:bg-red-700 transition-all duration-150 ease-linear outline-none focus:outline-none",
        purple:
          "bg-purple-500 text-white font-bold uppercase text-xs shadow hover:shadow-md active:bg-purple-600 transition-all duration-150 ease-linear outline-none focus:outline-none",
        amber:
          "bg-amber-400 text-white font-bold uppercase text-xs shadow hover:shadow-md active:bg-amber-500 transition-all duration-150 ease-linear outline-none focus:outline-none",
        cta: "bg-blue-600 text-white hover:bg-blue-700 font-bold uppercase",
        // Fleet Portal variants
        fleetPrimary:
          "bg-[hsl(var(--fleet-primary))] text-white hover:bg-[hsl(var(--fleet-primary-hover))] shadow-sm",
        fleetSecondary:
          "border border-[hsl(var(--fleet-primary))] text-[hsl(var(--fleet-primary))] bg-transparent hover:bg-[hsl(var(--fleet-primary))]/10",
        fleetSuccess:
          "bg-[hsl(var(--fleet-authorized))] text-white hover:bg-[hsl(var(--fleet-authorized))]/90 shadow-sm",
        fleetDanger:
          "bg-[hsl(var(--fleet-cancelled))] text-white hover:bg-[hsl(var(--fleet-cancelled))]/90 shadow-sm",
        fleetGhost:
          "text-[hsl(var(--fleet-primary))] hover:bg-[hsl(var(--fleet-surface))]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        // Legacy sizes
        legacy: "px-4 py-2 rounded",
        legacySm: "px-3 py-1 rounded text-xs",
        legacyIcon: "w-8 h-8 rounded-full p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
